/**
 * Regras da tela de cotação, sem React — o que dá para conferir sem renderizar.
 *
 * A AUTORIDADE é a edge function `seguradoras-cotacao` (que por sua vez confere
 * contra a Azos). O que existe aqui é resposta imediata para o usuário: marcar
 * o capital em vermelho enquanto ele digita, em vez de esperar uma ida à API
 * para dizer "múltiplo de 10". Por isso a cópia é pequena de propósito: se as
 * duas divergirem, quem manda é a de lá, e o pior caso é a tela deixar passar
 * um valor que a API recusa com a mensagem certa.
 */

import { isValidCpf } from "../contacts/document-validation";
import type { CodigoSeguradora } from "./seguradoras";

export interface Profissao {
  id: string;
  nome: string;
}

export interface CoberturaElegivel {
  codigo: string;
  slug: string | null;
  nome: string | null;
  disponivel: boolean;
  vendeSozinha: boolean;
  exigeCoberturas: string[];
  capitalMaximo: number | null;
  multiploCapital: number | null;
  /** A MAG já diz com que capital começar (funeral é quase fixo; assistência não depende de capital). */
  capitalSugerido?: number;
  /** Produto a que a cobertura pertence (MAG): a tela agrupa por ele, porque "MORTE" existe em vários. */
  grupo?: string;
  /** MAG: vai sempre que o produto vai — no YZ o produto de vida é um pacote. */
  obrigatoria?: boolean;
  /** MAG: regras do produto que a proposta precisa (declaração de saúde, beneficiário). */
  exigeDPS?: boolean;
  indicarBeneficiario?: boolean;
}

export interface ItemCesta {
  codigo: string;
  capital: number;
}

export interface LinhaPremio {
  codigo: string;
  nome: string | null;
  capital: number | null;
  premio: number | null;
  erro: string | null;
}

export interface Desconto {
  percentual: number | null;
  premioComDesconto: number | null;
  anualCheio: number | null;
  anualComDesconto: number | null;
}

export interface ResultadoCotacao {
  linhas: LinhaPremio[];
  premioTotal: number | null;
  desconto: Desconto | null;
  todasInvalidas: boolean;
  /** O que não impede o preço mas impede a venda — ex.: abaixo do mínimo da oferta da MAG. */
  avisos?: string[];
}

export interface Perfil {
  nascimento: string;
  sexo: "m" | "f";
  altura: number;
  peso: number;
  fumante: boolean;
  salario: number;
  profissaoId: string;
  // Só a MAG pergunta o que vem abaixo: UF, profissão pela lista CBO dela e a
  // declaração de IR. Nome e CPF são opcionais para cotar (ela cota sem) e
  // obrigatórios na proposta; CPF preenchido precisa ser válido.
  nome?: string;
  cpf?: string;
  uf?: string;
  /** Id na lista de profissões da MAG (`<CBO>#<Id>`) — outra lista, não a da Azos. */
  profissaoMagId?: string;
  /** 1 não declara · 2 simplificada · 3 completa (domínio da MAG) */
  declaracaoIR?: number;
}

export const PERFIL_VAZIO: Perfil = {
  nascimento: "",
  sexo: "m",
  altura: 0,
  peso: 0,
  fumante: false,
  salario: 0,
  profissaoId: "",
  nome: "",
  cpf: "",
  uf: "",
  profissaoMagId: "",
  declaracaoIR: 0,
};

export const UFS = [
  "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG", "PA",
  "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO",
] as const;

export const DECLARACOES_IR: { id: number; nome: string }[] = [
  { id: 1, nome: "Não declara" },
  { id: 2, nome: "Declaração simplificada" },
  { id: 3, nome: "Declaração completa" },
];

/** Idade em anos completos; usada no resumo do perfil. */
export const idade = (nascimento: string, hoje = new Date()): number | null => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(nascimento)) return null;
  const n = new Date(`${nascimento}T00:00:00Z`);
  if (Number.isNaN(n.getTime())) return null;
  let anos = hoje.getUTCFullYear() - n.getUTCFullYear();
  const mes = hoje.getUTCMonth() - n.getUTCMonth();
  if (mes < 0 || (mes === 0 && hoje.getUTCDate() < n.getUTCDate())) anos--;
  return anos;
};

/**
 * Número digitado à brasileira: vírgula é decimal e ponto é milhar. `Number()`
 * sozinho devolve NaN para "1,90" e 190 para "1.90", que é pior — vira um valor
 * plausível e errado.
 */
export const numeroBr = (texto: string): number | null => {
  const limpo = texto.trim().replace(/\s/g, "");
  if (!limpo) return null;

  let normal: string;
  if (limpo.includes(",")) {
    // Tem vírgula: ela é o decimal e todo ponto é milhar. "1.234,56" → 1234.56
    normal = limpo.replace(/\./g, "").replace(",", ".");
  } else if (/^\d{1,3}(\.\d{3})+$/.test(limpo)) {
    // Só pontos, e todos separando grupos de três: é milhar. "12.000" → 12000
    normal = limpo.replace(/\./g, "");
  } else {
    // Um ponto sozinho com 1, 2 ou 4+ casas é decimal — "1.78" é altura, não
    // mil setecentos e oitenta.
    normal = limpo;
  }

  const n = Number(normal);
  return Number.isFinite(n) ? n : null;
};

/**
 * Faixa que a Azos aceita, em metros (azos/cotacao.ts:107). Conferir aqui
 * também não é duplicar regra por gosto: quem digita 190 pensando em
 * centímetros passava por este filtro, ia até a seguradora e voltava com um 400
 * que a tela nem mostrava. A conta de IMC entregava o absurdo — 90 kg em 190 m
 * dá "IMC 0" — e ninguém era avisado.
 */
export const ALTURA_MIN_M = 0.5;
export const ALTURA_MAX_M = 2.5;

/**
 * IMC arredondado a uma casa; null quando falta altura ou peso, ou quando a
 * altura está fora da faixa humana — aí a conta só produz um número que parece
 * dado ("IMC 0" para 190 m) e esconde o erro de digitação.
 */
export const imc = (altura: number, peso: number): number | null => {
  if (!(altura > 0) || !(peso > 0)) return null;
  if (altura < ALTURA_MIN_M || altura > ALTURA_MAX_M) return null;
  return Math.round((peso / (altura * altura)) * 10) / 10;
};

/**
 * Altura em metros a partir do que a pessoa digitou.
 *
 * O campo diz "m" e mesmo assim 190 foi digitado — é o hábito de dizer a altura
 * em centímetros. Entre 50 e 250 não existe leitura ambígua: ninguém tem 190
 * metros nem 1,9 centímetro, então converter é ler a intenção, não adivinhar.
 * Fora dessa janela nada é mexido, e a validação reclama.
 */
export const alturaEmMetros = (valor: number): number => {
  if (valor >= 50 && valor <= 250) return Math.round(valor) / 100;
  return valor;
};

/**
 * O pedaço do perfil que cada seguradora recebe. Identidade (nome, CPF) só
 * segue para a MAG, que não simula sem ela; medidas e fumo só para a Azos.
 */
export const perfilPara = (seguradora: CodigoSeguradora, p: Perfil): Record<string, unknown> =>
  seguradora === "mag"
    ? {
        nascimento: p.nascimento,
        sexo: p.sexo,
        salario: p.salario,
        nome: p.nome ?? "",
        cpf: p.cpf ?? "",
        uf: p.uf ?? "",
        profissaoMagId: p.profissaoMagId ?? "",
        declaracaoIR: p.declaracaoIR ?? 0,
      }
    : {
        nascimento: p.nascimento,
        sexo: p.sexo,
        altura: p.altura,
        peso: p.peso,
        fumante: p.fumante,
        salario: p.salario,
        profissaoId: p.profissaoId,
      };

/**
 * Campos que faltam para poder pedir as coberturas. Vazio = pode.
 *
 * Depende de quem vai cotar: a Azos precisa de medidas e da profissão da
 * lista dela; a MAG precisa de UF, profissão da lista CBO e IR. Nascimento,
 * sexo e renda servem às duas. Nome e CPF são livres na cotação — só um CPF
 * preenchido e inválido barra, porque a MAG recusa a cotação inteira.
 */
export const problemasDoPerfil = (
  p: Perfil,
  seguradoras: Iterable<CodigoSeguradora> = ["azos"],
): string[] => {
  const quem = new Set(seguradoras);
  const faltando: string[] = [];
  if (quem.has("mag") && (p.cpf ?? "").replace(/\D/g, "") && !isValidCpf(p.cpf ?? "")) {
    faltando.push("CPF válido (opcional)");
  }
  if (idade(p.nascimento) === null) faltando.push("data de nascimento");
  if (quem.has("azos")) {
    if (!(p.altura > 0)) faltando.push("altura");
    else if (p.altura < ALTURA_MIN_M || p.altura > ALTURA_MAX_M) {
      faltando.push("altura em metros (ex.: 1,90)");
    }
    if (!(p.peso > 0)) faltando.push("peso");
  }
  if (!(p.salario > 0)) faltando.push("renda mensal");
  if (quem.has("azos") && !p.profissaoId) faltando.push("profissão");
  if (quem.has("mag")) {
    if (!p.uf || !(UFS as readonly string[]).includes(p.uf)) faltando.push("UF");
    if (!p.profissaoMagId) faltando.push(quem.has("azos") ? "profissão na lista da MAG" : "profissão");
    if (![1, 2, 3].includes(Number(p.declaracaoIR))) faltando.push("declaração de IR");
  }
  return faltando;
};

/**
 * Capital inicial ao marcar uma cobertura: um valor redondo que já respeita o
 * múltiplo e o teto. Evita que a primeira cotação de todo mundo já volte com erro.
 */
export const capitalSugerido = (cob: CoberturaElegivel): number => {
  if (cob.capitalSugerido != null && cob.capitalSugerido > 0) return cob.capitalSugerido;
  const teto = cob.capitalMaximo ?? 0;
  const multiplo = cob.multiploCapital && cob.multiploCapital > 0 ? cob.multiploCapital : 1;
  if (teto <= 0) return multiplo;
  const metade = Math.floor(teto / 2 / multiplo) * multiplo;
  return metade > 0 ? metade : Math.floor(teto / multiplo) * multiplo || multiplo;
};

/**
 * Marca ou desmarca uma cobertura, preservando o capital já digitado.
 *
 * Pacote (MAG): marcar qualquer cobertura de um produto que tem obrigatórias
 * traz as obrigatórias junto; desmarcar uma obrigatória tira o produto
 * inteiro. A MAG não aceita o produto pela metade, então a tela nem deixa
 * montar assim. `todas` é a lista da seguradora (sem ela, cobertura avulsa).
 */
export const alternarCobertura = (
  cesta: ItemCesta[],
  cob: CoberturaElegivel,
  todas: CoberturaElegivel[] = [],
): ItemCesta[] => {
  const existe = cesta.some((i) => i.codigo === cob.codigo);
  const doPacote = cob.grupo ? todas.filter((c) => c.grupo === cob.grupo && c.obrigatoria) : [];
  if (existe) {
    if (!cob.obrigatoria || !doPacote.length) return cesta.filter((i) => i.codigo !== cob.codigo);
    const doGrupo = new Set(todas.filter((c) => c.grupo === cob.grupo).map((c) => c.codigo));
    return cesta.filter((i) => !doGrupo.has(i.codigo));
  }
  const novas = [cob, ...doPacote].filter(
    (c, i, lista) => lista.findIndex((o) => o.codigo === c.codigo) === i && !cesta.some((it) => it.codigo === c.codigo),
  );
  return [...cesta, ...novas.map((c) => ({ codigo: c.codigo, capital: capitalSugerido(c) }))];
};

export const definirCapital = (cesta: ItemCesta[], codigo: string, capital: number): ItemCesta[] =>
  cesta.map((i) => (i.codigo === codigo ? { ...i, capital } : i));

/**
 * Problema de UMA linha da cesta, para marcar o campo enquanto digita.
 * null = sem problema conhecido.
 */
export const problemaDaLinha = (item: ItemCesta, cob: CoberturaElegivel | undefined): string | null => {
  if (!cob) return "cobertura fora da lista deste perfil";
  if (!cob.disponivel) return "indisponível para este perfil";
  if (!(item.capital > 0)) return "capital não informado";
  if (cob.capitalMaximo != null && item.capital > cob.capitalMaximo) {
    return `máximo ${formatarCapital(cob.capitalMaximo)}`;
  }
  if (cob.multiploCapital && item.capital % cob.multiploCapital !== 0) {
    return `múltiplo de ${cob.multiploCapital}`;
  }
  return null;
};

/** Tudo que impede de calcular o prêmio, incluindo dependências entre coberturas. */
export const problemasDaCesta = (cesta: ItemCesta[], coberturas: CoberturaElegivel[]): string[] => {
  if (!cesta.length) return ["Selecione ao menos uma cobertura."];
  const porCodigo = new Map(coberturas.map((c) => [c.codigo, c]));
  const naCesta = new Set(cesta.map((i) => i.codigo));
  const problemas: string[] = [];
  // Pacote pela metade (só acontece se a cesta veio de fora da tela).
  const grupos = new Set(cesta.map((i) => porCodigo.get(i.codigo)?.grupo).filter(Boolean));
  for (const grupo of grupos) {
    const faltam = coberturas.filter((c) => c.grupo === grupo && c.obrigatoria && !naCesta.has(c.codigo));
    if (faltam.length) problemas.push(`${grupo}: o produto deve ser contratado completo (faltam ${faltam.map((c) => c.nome).join(", ")})`);
  }
  for (const item of cesta) {
    const cob = porCodigo.get(item.codigo);
    const nome = cob?.nome ?? item.codigo;
    const linha = problemaDaLinha(item, cob);
    if (linha) problemas.push(`${nome}: ${linha}`);
    if (!cob) continue;
    const faltando = cob.exigeCoberturas.filter((pai) => !naCesta.has(pai));
    if (faltando.length) {
      problemas.push(`${nome}: depende de ${faltando.map((c) => porCodigo.get(c)?.nome ?? c).join(", ")}`);
    }
    if (!cob.vendeSozinha && !cob.exigeCoberturas.length && cesta.length === 1) {
      problemas.push(`${nome}: não pode ser contratada sozinha`);
    }
  }
  return problemas;
};

/** Separa o que a seguradora aceitou do que recusou, para a tela não misturar. */
export const resumoResultado = (r: ResultadoCotacao | null) => {
  if (!r) return { aceitas: [] as LinhaPremio[], recusadas: [] as LinhaPremio[], economiaAnual: null as number | null };
  const aceitas = r.linhas.filter((l) => !l.erro);
  const recusadas = r.linhas.filter((l) => l.erro);
  const economiaAnual =
    r.desconto?.anualCheio != null && r.desconto?.anualComDesconto != null
      ? Math.round((r.desconto.anualCheio - r.desconto.anualComDesconto) * 100) / 100
      : null;
  return { aceitas, recusadas, economiaAnual };
};

/** Filtro do campo de profissão: sem acento, sem caixa, no máximo `limite`. */
export const filtrarProfissoes = (lista: Profissao[], termo: string, limite = 50): Profissao[] => {
  const t = normalizar(termo);
  if (!t) return lista.slice(0, limite);
  return lista.filter((p) => normalizar(p.nome).includes(t)).slice(0, limite);
};

const normalizar = (s: string) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();

export const formatarCapital = (v: number | null | undefined): string =>
  v == null ? "—" : v.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });
